import { Box, Tab, Tabs } from "@mui/material";
import { UserPlus, UserRoundPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { useProject } from "../../common/contexts/ProjectContext";
import { getMemberFinancials, removeProjectMember } from "../api/members";
import AddMemberModal from "../components/AddMemberModal";
import ContentMappingTab from "../components/ContentMappingTab";
import EditMemberModal from "../components/EditMemberModal";
import InviteUserModal from "../components/InviteUserModal";
import SettingsTab from "../components/SettingsTab";
import SubMembersTab from "../components/SubMembersTab";
import TeamTab from "../components/TeamTab";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`members-tabpanel-${index}`}
      aria-labelledby={`members-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

function Members() {
  const { t } = useTranslation("members");
  const { project, setProject, refreshProject, currentUserRole } = useProject();
  const [activeTab, setActiveTab] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [financials, setFinancials] = useState({});

  const isOwner = currentUserRole === "owner";

  useEffect(() => {
    if (isOwner) {
      getMemberFinancials().then(setFinancials);
    }
  }, [isOwner]);

  // Guard: if not owner and tab > 0, reset
  useEffect(() => {
    if (!isOwner && activeTab > 0) {
      setActiveTab(0);
    }
  }, [isOwner, activeTab]);

  const handleRemove = async (user) => {
    if (!user) return;
    try {
      await removeProjectMember(user.id);
      toast.success(t("messages.removedSuccess"));
      setProject({
        ...project,
        users: project?.users?.filter((u) => u.id !== user.id),
      });
    } catch (error) {
      toast.error(t("messages.removedError"));
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedUser(null);
  };

  return (
    <Box>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          ["owner", "producer", "co_producer"].includes(currentUserRole) ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              {isOwner && (
                <Button
                  variant="contained"
                  startIcon={<UserRoundPlus size={18} />}
                  onClick={() => setShowAddMemberModal(true)}
                >
                  {t("addMember.button")}
                </Button>
              )}
              <Button
                variant="secondary"
                startIcon={<UserPlus size={18} />}
                onClick={() => setShowInviteModal(true)}
              >
                {t("inviteUser")}
              </Button>
            </Box>
          ) : null
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t("tabs.team")} />
          {isOwner && <Tab label={t("tabs.contentMapping")} />}
          {isOwner && <Tab label={t("tabs.subMembers")} />}
          {isOwner && <Tab label={t("tabs.settings")} />}
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <TeamTab
          project={project}
          currentUserRole={currentUserRole}
          financials={financials}
          onEdit={handleEdit}
          onRemove={handleRemove}
        />
      </TabPanel>

      {isOwner && (
        <TabPanel value={activeTab} index={1}>
          <ContentMappingTab project={project} onEdit={handleEdit} />
        </TabPanel>
      )}

      {isOwner && (
        <TabPanel value={activeTab} index={2}>
          <SubMembersTab project={project} />
        </TabPanel>
      )}

      {isOwner && (
        <TabPanel value={activeTab} index={3}>
          <SettingsTab />
        </TabPanel>
      )}

      <AddMemberModal
        open={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onMemberAdded={() => refreshProject()}
        currentUserRole={currentUserRole}
      />

      <InviteUserModal
        showInviteModal={showInviteModal}
        setShowInviteModal={setShowInviteModal}
        currentUserRole={currentUserRole}
      />

      <EditMemberModal
        project={project}
        setProject={setProject}
        showEditMemberModal={showEditModal}
        setShowEditMemberModal={handleEditModalClose}
        selectedMember={selectedUser}
      />
    </Box>
  );
}

export default Members;
